import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function MertonCouncilPage() {
  const faqItems = [
    {
      question: 'Merton spends nearly eight times its DfT capital allocation — can I still claim?',
      answer: 'Yes. The DfT Spend scorecard is GREEN — projected capital spend of £4.72m in 2025/26 against a £606,000 DfT allocation, with 83% classed as preventative. But the DfT Condition scorecard is RED, and Merton\'s own AI survey data shows B&C and U-road condition declining since 2022. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
    },
    {
      question: 'What if my pothole was on a residential street in Merton?',
      answer: 'Unclassified roads make up 287.2km — 79.4% of Merton\'s 361.8km carriageway network. At the 2024 survey, 16.6% of U-roads were rated Very Poor and 27.2% Poor — meaning 43.8% of residential streets need maintenance now or soon. The council undertakes annual AI condition surveys on U-roads, but the recorded condition is a snapshot in time and can vary along a road\'s length.',
    },
    {
      question: 'Merton only has three years of AI survey data — does that matter?',
      answer: 'It can. Merton states that prior road condition assessments used a different provider and methodology, and the resulting data "is not suitable for reliable trend analysis." AI-led surveys were introduced from 2022. The DfT expects five years of condition data; Merton publishes 2022, 2023 and 2024 only under the current system. Your claim should focus on the specific defect, prior reports and photos — not borough-wide percentages alone.',
    },
    {
      question: 'The council admits B&C and U-road condition has declined — how does that help?',
      answer: 'Merton\'s transparency report states that since 2022, "the overall condition of our classified non-principal and unclassified roads has declined, highlighting the need for additional highway maintenance." Very Poor U-roads rose from 13.8% in 2023 to 16.6% in 2024, and Poor-or-worse share climbed from 40.7% to 43.8%. That is documented knowledge of network deterioration — which raises questions about whether your specific defect was caught in time.',
    },
    {
      question: 'Does Merton\'s Section 58 streetworks notice block my pothole claim?',
      answer: 'No. Merton publishes Section 58 notices under the New Roads and Street Works Act 1991 to protect newly resurfaced roads from utility excavations — restrictions of one to three years depending on works. Your pothole claim relies on Section 58 of the Highways Act 1980: whether the council took reasonable care to maintain the specific road. The two provisions share a number, not a defence.',
    },
    {
      question: 'Merton filled 1,683 potholes in 2024/25 — does that mean the roads are fixed?',
      answer: 'No. Merton filled an estimated 1,683 carriageway defects in 2024/25, up from 1,415 in 2021/22, and expects to fix approximately 1,600 potholes in 2025/26 based on historical trends. The council plans to resurface just 10km of its 361.8km network in 2025/26 — rising to 13km with additional DfT funding. Reactive pothole filling on a RED-rated network does not eliminate liability for individual defects that formed and persisted between inspections.',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Merton Pothole Claims | DfT RED Condition | 16.6% U-Roads Very Poor | Fixtyer"
        description="Merton Council earns RED on road condition despite GREEN spend — 16.6% of residential roads rated Very Poor in 2024, B&C and U-road condition declining since 2022. See what this means for your pothole claim."
        canonical="/councils/merton"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Merton', url: '/councils/merton' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      <ServiceSchema
        councilName="Merton Council"
        councilSlug="merton"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/merton"
        headline="Merton Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Merton</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Nearly One in Six Merton Residential Roads Is Very Poor
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            DfT rates Merton <strong className="text-rose-400">RED on condition</strong> — among the weakest
            road scores in London — kept to <strong className="text-amber-400">AMBER</strong> overall only by
            a <strong className="text-emerald-400">GREEN</strong> spend rating. The council's own AI surveys
            show <strong className="text-rose-400">16.6% of 287.2km of U-roads rated Very Poor</strong> in 2024,
            B&C and residential condition declining since 2022, and{' '}
            <strong className="text-rose-400">6,663 carriageway defects filled in four years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">16.6%</div>
            <div className="text-white font-medium mt-2">U-roads rated Very Poor (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 13.8% in 2023 on 287.2km of residential streets — 79.4% of Merton's entire
              361.8km carriageway network. A further 27.2% were rated Poor, meaning maintenance
              "will be required" under the council's own definitions.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The RED Condition Rating Means
          </h2>
          <p className="text-surface-600 mb-8">
            Three years of AI survey data from Merton's own transparency report — B&C and U-roads
            deteriorating since 2022
          </p>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">
              Unclassified roads (287.2km — 79.4% of network): declining
            </h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Very Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-600">Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Fair</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Good</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-800">Very Good</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">15.3%</td>
                    <td className="px-4 py-2 text-center">25.5%</td>
                    <td className="px-4 py-2 text-center">28.2%</td>
                    <td className="px-4 py-2 text-center">15.1%</td>
                    <td className="px-4 py-2 text-center">15.8%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">13.8%</td>
                    <td className="px-4 py-2 text-center">26.9%</td>
                    <td className="px-4 py-2 text-center">29.2%</td>
                    <td className="px-4 py-2 text-center">16.3%</td>
                    <td className="px-4 py-2 text-center">13.8%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">16.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">27.2%</td>
                    <td className="px-4 py-2 text-center font-bold">28.5%</td>
                    <td className="px-4 py-2 text-center font-bold">15.8%</td>
                    <td className="px-4 py-2 text-center font-bold">12.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              Very Poor U-roads rose from 13.8% to 16.6% in a single survey cycle. Combined Poor and
              Very Poor share climbed from 40.7% to 43.8% — nearly half of Merton's residential network
              where the council defines maintenance as required or imminent.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">
              B and C roads (38.5km — 10.6% of network): also declining
            </h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Very Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-600">Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Fair</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Good</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-800">Very Good</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">8.0%</td>
                    <td className="px-4 py-2 text-center">24.9%</td>
                    <td className="px-4 py-2 text-center">31.8%</td>
                    <td className="px-4 py-2 text-center">19.2%</td>
                    <td className="px-4 py-2 text-center">16.1%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.7%</td>
                    <td className="px-4 py-2 text-center">27.3%</td>
                    <td className="px-4 py-2 text-center">32.7%</td>
                    <td className="px-4 py-2 text-center">19.4%</td>
                    <td className="px-4 py-2 text-center">14.9%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.9%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">28.4%</td>
                    <td className="px-4 py-2 text-center font-bold">33.3%</td>
                    <td className="px-4 py-2 text-center font-bold">18.3%</td>
                    <td className="px-4 py-2 text-center font-bold">12.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Poor-or-worse B&C roads rose from 32.9% in 2022 to 36.3% in 2024. Very Good share fell
              from 16.1% to 12.1% — the classified local network is slipping even as A-roads hold
              relatively steady.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">
              A-roads (36.1km — 10% of network): surveyed via TfL
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">10.6%</div>
                <div className="text-sm text-rose-800">Very Poor (2024)</div>
                <div className="text-xs text-surface-500">down from 13.1% in 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">11.0%</div>
                <div className="text-sm text-amber-800">Very Poor (2022)</div>
                <div className="text-xs text-surface-500">baseline year for AI surveys</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-surface-600">—</div>
                <div className="text-sm text-surface-700">2020 and 2021</div>
                <div className="text-xs text-surface-500">no surveys — Covid-19 restrictions</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads are surveyed annually as part of the Borough Principal Road Network survey TfL
              undertakes. Principal roads are a tenth of the network — the RED DfT condition rating
              reflects what is happening on the other 90%.
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Since 2022, the overall condition of our classified non-principal (Figure 4) and unclassified roads (Figure 5) has declined, highlighting the need for additional highway maintenance to improve the overall condition of our roads."
              source="Merton Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Very Poor: Severe deterioration, maintenance will be required"
              source="Merton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — RED Condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£606k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.72m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">83%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Merton spends nearly <span className="text-emerald-400">eight times its DfT allocation</span> — and
              the DfT still rates it <span className="text-rose-400">RED on condition</span>. The council plans
              to resurface just 10km of its 361.8km network in 2025/26 — 13km with additional DfT funding.
              Money is not the bottleneck. Measured road condition is.
            </p>
          </div>
        </div>
      </section>

      {/* The 287km Residential Network */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 287.2km Where Most Claims Happen
          </h2>
          <p className="text-surface-600 mb-8">
            Nearly four-fifths of Merton's carriageway network is unclassified residential roads
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey frequency</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A-roads (Principal)</td>
                  <td className="px-6 py-4 text-center font-mono">36.1</td>
                  <td className="px-6 py-4 text-center">10.0%</td>
                  <td className="px-6 py-4 text-center">Annual (TfL BPRN survey)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">38.5</td>
                  <td className="px-6 py-4 text-center">10.6%</td>
                  <td className="px-6 py-4 text-center">Annual (AI survey)</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-bold text-brand-900">U-roads (Unclassified)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">287.2</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">79.4%</td>
                  <td className="px-6 py-4 text-center">Annual (AI survey)</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">361.8</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">How Merton Defines Condition</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>Very Good / Good</strong> — no deterioration, no treatment required</li>
                <li>• <strong>Fair</strong> — moderate deterioration, maintenance may be required soon</li>
                <li>• <strong>Poor</strong> — moderate to severe deterioration, maintenance will be required</li>
                <li>• <strong>Very Poor</strong> — severe deterioration, maintenance will be required</li>
              </ul>
              <p className="text-surface-600 text-sm mt-3">
                Merton uses five AI-derived categories — not the DfT's three-colour RED/Amber/Green
                scorecard labels. The DfT's RED condition rating is a separate national assessment.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Three-Year Baseline</h3>
              <p className="text-amber-800 text-sm mb-3">
                AI-led condition surveys were introduced in Merton from 2022. The council states prior
                assessments used a different provider and methodology, and the resulting data "is not
                suitable for reliable trend analysis."
              </p>
              <p className="text-amber-800 text-sm">
                You have three comparable survey years — 2022, 2023 and 2024 — all showing B&C and
                U-road deterioration. That is enough to establish a documented downward trend, even
                without a five-year history under the current system.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="It is important to note that the recorded condition of our network is a snapshot in time. The reported condition of a section of road can vary across its length, due to multiple factors like repairs from utility repairs and areas of increased vehicle loading, such as bus stops."
              source="Merton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Merton must show it had a reasonable system for
              knowing the condition of its roads. For the borough's 287.2km of U-roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• If 16.6% of U-roads were Very Poor at the last survey, what was done about yours?</li>
              <li>• The council admits B&C and U-road condition has declined since 2022 — was your defect on a deteriorating section?</li>
              <li>• Condition varies along a road's length — did the survey capture the specific defect location?</li>
              <li>• Annual AI surveys produce network-level data — did that translate into timely repair of your pothole?</li>
            </ul>
            <p className="text-white font-medium">
              A council that documents declining residential road condition cannot claim blanket
              ignorance of the network state — your claim turns on whether they acted on what they knew.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            6,663 Carriageway Defects in Four Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes on a network the DfT rates RED on condition
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway defects filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Resurfacing completed (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive spend share</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,415</td>
                  <td className="px-6 py-4 text-center">9.2</td>
                  <td className="px-6 py-4 text-center">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,635</td>
                  <td className="px-6 py-4 text-center">7.0</td>
                  <td className="px-6 py-4 text-center">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,930</td>
                  <td className="px-6 py-4 text-center">6.6</td>
                  <td className="px-6 py-4 text-center">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">1,683</td>
                  <td className="px-6 py-4 text-center">10.2</td>
                  <td className="px-6 py-4 text-center">22%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">~1,600</td>
                  <td className="px-6 py-4 text-center font-bold">13.7</td>
                  <td className="px-6 py-4 text-center font-bold">17%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,663</td>
                  <td className="px-6 py-4 text-center font-bold">33.0</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Potholes Outpace Resurfacing</h3>
              <p className="text-surface-600 text-sm">
                Merton filled 1,930 carriageway defects in 2023/24 — its highest four-year figure — while
                resurfacing just 6.6km that year on a 361.8km network. Even the boosted 13.7km planned
                for 2025/26 treats under 4% of the network annually. Defects form faster than structural
                renewal can catch up.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Council's Own Forecast</h3>
              <p className="text-amber-800 text-sm">
                Merton expects to fix approximately 1,600 potholes in 2025/26 based on historical trends —
                while planning 83% of its highways budget on planned maintenance and 17% on reactive
                day-to-day defect response under its statutory duty to keep the highway safe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Survey Methodology */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            AI Surveys — Innovation With Limits
          </h2>
          <p className="text-surface-600 mb-8">
            Merton's inspection approach and what the council says about it
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Road condition assessments on the local classified and unclassified road network in Merton are currently undertaken using Artificial Intelligence (AI) led surveys. This aligns with the approach Transport for London (TfL) take on our Borough Principal Road Network (BPRN). The AI survey detects the condition of a road from video footage and provides an objective consistent approach to determining the condition across our network."
              source="Merton Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Prior assessments of road conditions were conducted using a different provider and methodology. Consequently, the resulting data is not suitable for reliable trend analysis."
              source="Merton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Risk-Based Inspection Hierarchy</h3>
              <p className="text-surface-600 text-sm mb-3">
                Merton uses hierarchy risk ratings based on vehicular, pedestrian and cyclist volumes,
                transport hub locations, emergency service access, schools and event venues. These ratings
                determine inspection frequency and response times for safety-critical defects.
              </p>
              <p className="text-surface-600 text-sm">
                Resurfacing prioritisation considers condition survey results, historical defect locations,
                community feedback and highway officer judgement — ranked schemes receive funding first.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">AMBER Best Practice</h3>
              <p className="text-amber-800 text-sm">
                The DfT Best Practice scorecard is AMBER — not RED like Sutton — but Merton's report
                publishes only three years of AI survey data when the framework expects five. The council
                participates in London Highways Engineers Group forums and uses investment modelling and
                business intelligence dashboards — credible systems, but the condition trend still points
                the wrong way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 58 streetworks */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Section 58 — Two Different Laws
          </h2>
          <p className="text-surface-600 mb-8">
            Merton issues streetworks notices and maintains highways under separate statutory duties
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We publish our planned works on our website (link). This also acts as a notice under Section 58 of the New Roads and Street Works Act 1991, to inform utility companies and developers to restrict works on certain roads within our borough following major road improvements, unless in emergency situations."
              source="Merton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Streetworks Section 58 (1991 Act)</h3>
              <p className="text-surface-600 text-sm">
                Protects newly resurfaced roads from utility excavations for one to three years. Merton
                coordinates quarterly meetings with utility companies and uses mapping tools to plan
                works — including successful coordination on Lower Green West in Mitcham.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Highways Act Section 58 (1980 Act)</h3>
              <p className="text-rose-800 text-sm">
                The defence Merton will raise against your pothole claim. Under Section 41, Merton has
                a legal requirement to maintain public highways. Section 58 lets the council argue it
                took reasonable care — but a RED condition rating, documented U-road decline and
                6,663 reactive repairs in four years undermine a blanket defence if your specific
                defect was identifiable and untreated.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded RED-Condition Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Merton invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend nearly eight times the DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 83% of projected 2025/26 spend classed as preventative maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual AI condition surveys on all B, C and U-roads since 2022
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Risk-based hierarchy system aligned with the Code of Practice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road Very Poor share fell from 13.1% to 10.6% between 2023 and 2024
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence backed by genuine investment. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What Works In Yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT RED condition rating — among the worst-measured in London
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 16.6% of U-roads Very Poor in 2024, up from 13.8% in 2023
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits B&C and U-road condition has declined since 2022
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 287.2km of residential roads — where most pothole damage occurs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 6,663 carriageway defects filled in four years — defects form between repairs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing treats under 4% of the network annually even with DfT boost
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and annual AI surveys, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, cite the 16.6% Very Poor rate and documented decline since 2022</li>
              <li>• Whether utility works or bus-stop loading explain localised deterioration the survey may have averaged out</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Merton's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Merton?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            RED condition on a well-funded network demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road Very Poor rate cited</li>
                <li>• No prior-report search</li>
                <li>• No decline-since-2022 argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 16.6% Very Poor U-road rate documented</li>
                <li>✅ Council's own decline admission cited</li>
                <li>✅ 6,663 defects in four years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Merton</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=merton"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Merton Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqItems.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.merton.gov.uk/sites/default/files/2025-06/Merton%20-%20Local%20Highways%20Maintenance%20Transparency%20Report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Merton Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
